import { useI18n } from '@i18n';
import {
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Tooltip,
} from '@mui/material';
import { export_jobs_enum } from '@predium/enums';
import { translateExportJobsEnum } from '@predium/i18n/client';
import { useCallback } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import { durationFromNow } from '@/utils/formatTime';

export type CompletedBatchExport = {
  id: number;
  created_at?: string | null;
};

type Props = {
  batchExport: CompletedBatchExport;
  onDownload: (asyncJobId: number) => void;
};

/**
 * A completed batch one-pager export in the downloads popover. The zip isn't a
 * regular `file` (it lives in the batch-export bucket), so it's listed from its
 * `async_job` row and re-downloaded via the dedicated presigned-URL query. It stays
 * available for the zip's retention window (90 days), so the user can re-download.
 */
export default function CompletedBatchExportRow({ batchExport, onDownload }: Props) {
  const { t, language } = useI18n();

  const handleDownload = useCallback(() => onDownload(batchExport.id), [onDownload, batchExport.id]);

  return (
    <ListItem sx={{ py: 1, px: 2.5, pr: 8 }} data-testid="completed-batch-export-item">
      <ListItemIcon sx={{ minWidth: 48 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'background.neutral',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon={ICONS.ZIP} width={24} height={24} />
        </Box>
      </ListItemIcon>
      <ListItemText
        sx={{ pr: 1 }}
        primary={
          <PTypography variant="subtitle2" sx={{ mb: 0.5, whiteSpace: 'nowrap' }}>
            {translateExportJobsEnum(export_jobs_enum.BATCH_ESG_ANALYSIS_PDF_EXPORT)}
          </PTypography>
        }
        secondary={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Iconify icon={ICONS.CLOCK} width={16} height={16} />
            <PTypography variant="caption" color="secondary">
              {batchExport.created_at ? durationFromNow(batchExport.created_at, language) : null}
            </PTypography>
          </Stack>
        }
      />
      <ListItemSecondaryAction>
        <Tooltip title={t('Downloads_Download')}>
          <IconButton size="small" onClick={handleDownload} data-testid="batch-download-button">
            <Iconify icon={ICONS.EXPORT} />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
}
